import {
  ACCOUNT_DOCUMENT_STATUS_DISPLAY,
  AccountDocumentDetailResponse,
  AccountDocumentStatusEnum,
} from '@monetize/types/app';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  useGetAccountById,
  useSendAccountDocument,
  useVoidAccountDocument,
} from '../../../api/accountsService';
import { handleApiErrorToast } from '../../../api/axios';
import { useGetById } from '../../../api/queryUtils';
import {
  MButton,
  MFlex,
  MHStack,
  MPageContainer,
  MPageHeader,
  MPageLoader,
  MTooltip,
} from '../../../components/Monetize';
import { getAccountDocumentList } from '../../../constants/routes';
import { useConfirmModal } from '../../../services/confirmModal';
import { AccountDocumentActions } from './AccountDocumentActions';
import { AccountDocumentContactSection } from './components/AccountDocumentContactSection';
import { AccountDocumentDetailSection } from './components/AccountDocumentDetailSection';
import { AccountDocumentPDF } from './components/AccountDocumentPDF';

export const AccountDocumentPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const accountDocumentId = params.accountDocumentId || '';
  const accountId = params.accountId || '';
  const {
    showConfirmModal,
    closeModal: closeConfirmModal,
    loading: isLoadingConfirmModal,
    setModalLoading: setLoadingConfirmModal,
  } = useConfirmModal();

  const {
    data: accountDocument,
    isLoading: isLoadingAccountDocument,
    refetch: refetchAccountDocument,
    isError: isErrorAccountDocument,
    error: errorAccountDocument,
  } = useGetById<AccountDocumentDetailResponse>(
    'accountDocuments',
    accountDocumentId,
    {
      enabled: !!accountDocumentId && !!accountId,
      refetchOnWindowFocus: false,
      endpointArgs: {
        accountId,
      },
    },
  );

  const { isLoading: isLoadingAccount, data: accountDetails } =
    useGetAccountById(accountId, {
      enabled: !!accountId,
      refetchOnWindowFocus: false,
      meta: {
        showErrorToast: true,
      },
    });

  const { isPending: isSendingAccountDocument, mutate: doSendAccountDocument } =
    useSendAccountDocument({
      onSuccess: () => {
        refetchAccountDocument();
      },
    });

  const {
    isPending: isVoidingAccountDocument,
    mutateAsync: doVoidAccountDocument,
  } = useVoidAccountDocument({
    onSuccess: () => {
      refetchAccountDocument();
    },
  });

  const handleSend = () => {
    doSendAccountDocument({
      accountId,
      accountDocumentId,
    });
  };

  const handleVoidEsign = () => {
    showConfirmModal({
      title: 'Void eSign',
      description:
        'This will cancel the signature request and mark the document as void. Signers will no longer be able to view or sign it. You can update the document and resend it for signature at any time.',
      onYes: async () => {
        setLoadingConfirmModal(true);
        try {
          await doVoidAccountDocument({
            accountId,
            accountDocumentId,
          });
          closeConfirmModal();
        } catch (error) {
          handleApiErrorToast(error);
        }
        setLoadingConfirmModal(false);
      },
      yesButton: 'Void eSign',
      yesBtnProps: {
        variant: 'delete',
      },
    });
  };

  const contactsReadyForSending = useMemo(() => {
    return (
      accountDocument?.contacts.primaryESignContacts.find(
        ({ internal }) => internal,
      ) &&
      accountDocument?.contacts.primaryESignContacts.find(
        ({ internal }) => !internal,
      )
    );
  }, [accountDocument?.contacts.primaryESignContacts]);

  useEffect(() => {
    if (isErrorAccountDocument) {
      handleApiErrorToast(errorAccountDocument);
      navigate(getAccountDocumentList(accountId));
    }
  }, [isErrorAccountDocument, navigate, accountId, errorAccountDocument]);

  if (
    isLoadingAccountDocument ||
    !accountDocument ||
    isLoadingAccount ||
    !accountDetails
  ) {
    return <MPageLoader />;
  }

  return (
    <MPageContainer height="auto">
      <MPageHeader
        hasBackButton
        title={accountDocument?.name}
        id={accountDocumentId}
        status={ACCOUNT_DOCUMENT_STATUS_DISPLAY[accountDocument?.status]}
        recordAuditInfo={accountDocument}
      >
        <MHStack gap={2}>
          {accountDocument?.status === AccountDocumentStatusEnum.SENT && (
            <MButton
              variant="delete"
              onClick={handleVoidEsign}
              isLoading={isSendingAccountDocument}
              isDisabled={!contactsReadyForSending}
            >
              Void eSign
            </MButton>
          )}
          {accountDocument?.status === AccountDocumentStatusEnum.CREATED && (
            <MTooltip
              label="Please fill in contacts before sending documents."
              isDisabled={!!contactsReadyForSending}
              placement="bottom-end"
            >
              <MButton
                variant="primary"
                onClick={handleSend}
                isLoading={isSendingAccountDocument}
                isDisabled={!contactsReadyForSending}
              >
                Send
              </MButton>
            </MTooltip>
          )}
          {accountDocument && (
            <AccountDocumentActions
              accountId={accountId}
              accountDocument={accountDocument}
            />
          )}
        </MHStack>
      </MPageHeader>
      <MFlex w="100%" flexDirection={{ base: 'column', md: 'row' }}>
        <AccountDocumentDetailSection
          account={accountDetails}
          accountDocument={accountDocument}
          isReadOnly={
            accountDocument.status !== AccountDocumentStatusEnum.CREATED
          }
          refetchAccountDocument={refetchAccountDocument}
        />
        <AccountDocumentContactSection
          accountDocument={accountDocument}
          isReadOnly={
            accountDocument.status !== AccountDocumentStatusEnum.CREATED
          }
          refetchAccountDocument={refetchAccountDocument}
          account={accountDetails}
        />
      </MFlex>
      <AccountDocumentPDF
        accountDocument={accountDocument}
        isReadOnly={
          accountDocument.status !== AccountDocumentStatusEnum.CREATED
        }
        refetchAccountDocument={refetchAccountDocument}
      />
    </MPageContainer>
  );
};
